// React imports
import React, { useEffect, useState } from 'react';

// MUI imports
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import * as graph from '@microsoft/microsoft-graph-client';

// External libraries
import axios from 'axios';
import queryString from 'query-string';
import { v4 as uuidv4 } from 'uuid';
import _ from 'lodash';

let _listener = null;
let _expiredListener = null;

function setConversationsListener(listener) {
    _listener = listener;
}

function setExpiredListener(listener) {
    _expiredListener = listener;
}

// --- NEW HELPER FOR BATCHING ---
async function processBatchRequests(client, requests) {
    // Microsoft Graph allows max 20 requests per batch
    const chunks = _.chunk(requests, 20);
    let allResponses = [];

    for (const chunk of chunks) {
        const batchBody = {
            requests: chunk.map((req, index) => ({
                id: String(index),
                method: 'GET',
                url: req
            }))
        };

        try {
            const result = await client.api('/$batch').post(batchBody);
            if (result && result.responses) {
                allResponses = allResponses.concat(result.responses);
            }
        } catch (e) {
            console.error('[outlook] batch request failed', e);
        }
    }
    return allResponses;
}

async function refreshConnection({ organizationId, accesstoken }) {
    try {
        const sessionId = window.localStorage.getItem('microsoft-session-id') || uuidv4();
        window.localStorage.setItem('microsoft-session-id', sessionId);

        const { data } = await axios.get(`/api/microsoft?organizationId=${organizationId}&accesstoken=${accesstoken}&sessionId=${sessionId}`);
        if (data.status === 'expired') {
            console.log('[outlook] extending token');
            const { data: extendResponse } = await axios.get(`/api/microsoft/extend?organizationId=${organizationId}&accesstoken=${accesstoken}&sessionId=${sessionId}`);

            if (extendResponse.status === 'success') {
                console.log('[outlook] extending token - done');
                const { data: newData } = await axios.get(`/api/microsoft?organizationId=${organizationId}&accesstoken=${accesstoken}&sessionId=${sessionId}`);
                console.log('[outlook] extending token - new data', newData);
                return newData;
            } else {
                return { status: 'expired' };
            }
        } else {
            return data;
        }
    } catch (e) {
        console.log('[outlook] error with getMyInfo', e);
    }
}

async function forceRefreshConnection({ organizationId, accesstoken }) {
    try {
        const sessionId = window.localStorage.getItem('microsoft-session-id') || uuidv4();
        window.localStorage.setItem('microsoft-session-id', sessionId);

        console.log('[outlook] forcing token refresh');
        const { data: extendResponse } = await axios.get(`/api/microsoft/extend?organizationId=${organizationId}&accesstoken=${accesstoken}&sessionId=${sessionId}`);

        if (extendResponse.status === 'success') {
            console.log('[outlook] forced token refresh - done');
            const { data: newData } = await axios.get(`/api/microsoft?organizationId=${organizationId}&accesstoken=${accesstoken}&sessionId=${sessionId}`);
            console.log('[outlook] forced token refresh - new data', newData);
            return newData;
        } else {
            return { status: 'expired' };
        }
    } catch (e) {
        console.log('[outlook] error with force refresh', e);
        throw e;
    }
}

async function disconnect({ organizationId, accesstoken }) {
    const sessionId = window.localStorage.getItem('microsoft-session-id') || uuidv4();
    window.localStorage.setItem('microsoft-session-id', sessionId);

    return await axios.delete(`/api/microsoft/?organizationId=${organizationId}&accesstoken=${accesstoken}&sessionId=${sessionId}`);
}

function LoginButton(props) {
    const { organizationId, accesstoken, onDone } = props;
    const [ loading, setLoading ] = useState(false);
    const [ error, setError ] = useState(null);

    const sessionId = window.localStorage.getItem('microsoft-session-id') || uuidv4();
    window.localStorage.setItem('microsoft-session-id', sessionId);

    function onConnect() {
        setError(null);
        setLoading(true);
        setTimeout(() => {
            setLoading(false);
        }, 500);
        if (window.location.hostname === 'localhost') {
            window.open(`/api/microsoft/login?organizationId=${organizationId}&accesstoken=${accesstoken}&sessionId=${sessionId}`, '_blank');
        } else {
            window.open(`https://app.monada.ai/api/microsoft/login?organizationId=${organizationId}&accesstoken=${accesstoken}&sessionId=${sessionId}`, '_blank');
        }
    }

    async function onMessage(event) {
        const parsed = queryString.parse(event.data);

        if (parsed.error_description) {
            setError(parsed.error_description);
        }

        if (parsed.code) {
            setLoading(true);
            try {
                const { data } = await axios.get(`/api/microsoft/acquireTokenByCode?organizationId=${organizationId}&accesstoken=${accesstoken}&code=${parsed.code}&sessionId=${sessionId}`);
            } catch (e) {}
            setLoading(false);
            onDone();
        }
    }

    useEffect(() => {
        const channel = new BroadcastChannel('monada-outlook');
        channel.addEventListener('message', onMessage);
        return () => {
            channel.removeEventListener('message', onMessage);
        }
    }, [ onMessage ]);

    return (
        <>
            <Button
                variant='contained'
                color='monada-button'
                onClick={onConnect}
                disabled={loading}
                sx={{ minWidth: 0 }}
            >
                {loading ? <CircularProgress size={24} /> : 'Connect to Outlook'}
            </Button>
            {error && (
                <Alert severity='error' sx={{ mt: 2 }}>
                    {error}
                </Alert>
            )}
        </>
    );
}

function messageToConversation(message) {
    // Transform attachments to match expected format
    const attachments = (message.attachments || []).map(att => ({
        id: att.id,
        name: att.name,
        size: att.size,
        contentType: att.contentType,
        isInline: att.isInline || false,
        inlineId: att.contentId || att['microsoft.graph.fileAttachment/contentId'] || null
    }));

    return {
        ...message,
        body: {
            content: message.body?.content || message.bodyPreview || '',
            html: message.body?.content || message.bodyPreview || '',
            contentType: message.body?.contentType || 'html'
        },
        globalMessageId: message.internetMessageHeaders?.find(h => h.name === 'Message-ID')?.value || '',
        hasAttachments: message.hasAttachments || (attachments.length > 0),
        attachments: attachments,
        // Preserve sender field if it exists, otherwise set it from message.from
        sender: message.sender || (message.from ? {
            emailAddress: { 
                address: message.from.emailAddress?.address || '',
                name: message.from.emailAddress?.name || ''
            }
        } : null),
        // Ensure recipient fields are in the correct format
        toRecipients: message.toRecipients ? message.toRecipients.map(r => ({
            emailAddress: { 
                address: r.emailAddress?.address || '',
                name: r.emailAddress?.name || ''
            }
        })) : [],
        ccRecipients: message.ccRecipients ? message.ccRecipients.map(r => ({
            emailAddress: { 
                address: r.emailAddress?.address || '',
                name: r.emailAddress?.name || ''
            }
        })) : [],
    };
}

async function getMessagesForConversation({ conversationId, internetMessageId, accesstoken }) {
    if (!accesstoken) return;

    const client = graph.Client.init({ authProvider: (done) => { done(null, accesstoken); } });

    let _messages = [];

    try {
        if (conversationId) {
            _messages = await client
                .api('/me/messages')
                .filter(`conversationId eq '${conversationId}'`)
                .top(1000)
                .expand('attachments')
                .select('id,conversationId,subject,body,bodyPreview,receivedDateTime,from,toRecipients,ccRecipients,flag,categories,isRead,internetMessageHeaders,hasAttachments,attachments')
                .headers({
                    'Prefer': 'outlook.body-content-type="html"'
                })
                .get();
        } else if (internetMessageId) {
            _messages = await client
                .api('me/messages')
                .filter(`internetMessageId eq '${internetMessageId}'`)
                .expand('attachments')
                .select('id,conversationId,subject,body,bodyPreview,receivedDateTime,from,toRecipients,ccRecipients,flag,categories,isRead,internetMessageHeaders,hasAttachments,attachments')
                .headers({
                    'Prefer': 'outlook.body-content-type="html"'
                })
                .get();
        }

        if (_messages.value.length === 0) {
            return null;
        }

        // For messages with attachments, ensure we have all attachment data
        for (const message of _messages.value) {
            if (message.hasAttachments && (!message.attachments || message.attachments.length === 0)) {
                try {
                    const attachments = await client
                        .api(`/me/messages/${message.id}/attachments`)
                        .get();
                    message.attachments = attachments.value || [];
                } catch (e) {
                    console.error('[outlook] Error fetching attachments for message', message.id, e);
                    message.attachments = [];
                }
            }
        }

        const sortedMessages =
            _.sortBy(_messages.value, a => new Date(a.receivedDateTime).getTime())
                .reverse()
                .map(m => messageToConversation(m));

        return sortedMessages.map(m => ({ ...m, unread: !m.isRead }));
    } catch (e) {
        if (e.code === 'ErrorInvalidIdMalformed') {
            throw new Error('invalid-request')
        }
        // Check if this is an expired token error
        const errorMessage = e?.message || e?.error?.message || JSON.stringify(e);
        const isExpiredError = errorMessage?.includes('Lifetime validation failed') || 
                              errorMessage?.includes('expired') ||
                              errorMessage?.includes('InvalidAuthenticationToken') ||
                              e?.statusCode === 401 ||
                              e?.code === 'InvalidAuthenticationToken';
        
        if (isExpiredError && _expiredListener) {
            console.log('[outlook] Token expired in getMessagesForConversation, calling expired listener');
            _expiredListener();
            // Don't throw the error - it's been handled by the listener
            // Return empty array to indicate no messages could be fetched due to expired token
            return [];
        }
        throw e;
    }
}

async function createDraft({ accesstoken, toRecipients, ccRecipients, bccRecipients, subject, body, attachments }) {
    if (!accesstoken) return null;

    const client = graph.Client.init({ authProvider: (done) => { done(null, accesstoken); } });
    const message = {
        body: {
            content: body,
            contentType: 'html'
        },
        toRecipients: toRecipients.map(r => ({ emailAddress: r })),
        ccRecipients: ccRecipients?.map(r => ({ emailAddress: r })),
        bccRecipients: bccRecipients?.map(r => ({ emailAddress: r })),
        subject,
    };
    const draft = await client.api('/me/messages').post(message);
    if (attachments) {
        await Promise.all(attachments.map(attachment =>
            client.api(`/me/messages/${draft.id}/attachments/`).post(transformAttachment(attachment))
        ));
    }
    return draft;
}

async function sendEmail({ accesstoken, toRecipients, ccRecipients, bccRecipients, subject, body, attachments }) {
    const draft = await createDraft({ accesstoken, toRecipients, ccRecipients, bccRecipients, subject, body, attachments });
    const client = graph.Client.init({ authProvider: (done) => { done(null, accesstoken); } });
    await client.api(`/me/messages/${draft.id}/send`).post();
    return draft;
}

async function sendReply({ conversationId, accesstoken, toRecipients, ccRecipients, body, attachments = [] }) {
    const client = graph.Client.init({ authProvider: (done) => { done(null, accesstoken); } });
    
    // Workaround: remove $orderby to avoid "The restriction or sort order is too complex for this operation."
    // Get all messages in the conversation, then sort client-side
    const messages = await client.api(`/me/messages?$filter=conversationId eq '${conversationId}'`).get();
    // Filter out draft messages (drafts don't support createReply operation)
    // Only sent/received messages can be replied to
    const nonDraftMessages = (messages.value || []).filter(m => !m.isDraft && m.receivedDateTime);
    
    if (nonDraftMessages.length === 0) {
        throw new Error('Cannot reply to a conversation that only contains draft messages. Please reply to a sent or received message instead.');
    }
    
    // Sort messages client-side by receivedDateTime desc
    const sortedMessages = nonDraftMessages.sort((a, b) => new Date(b.receivedDateTime) - new Date(a.receivedDateTime));
    const messageId = sortedMessages[0].id;

    // Create reply draft without body first - this allows Microsoft Graph to automatically include the original message
    const draft = await client.api(`/me/messages/${messageId}/createReply`).post({
        message: {
            toRecipients: toRecipients.map(r => ({ emailAddress: _.isString(r) ? { address: r } : r })),
            ccRecipients: ccRecipients?.map(r => ({ emailAddress: _.isString(r) ? { address: r } : r }))
        }
    });

    // Fetch the draft to get the body with the original message included
    const draftWithOriginal = await client.api(`/me/messages/${draft.id}`)
        .select('body')
        .headers({
            'Prefer': 'outlook.body-content-type="html"'
        })
        .get();

    // Prepend our new reply body to the existing body (which includes the original message)
    const originalBody = draftWithOriginal.body?.content || '';
    const combinedBody = body ? `${body}${originalBody}` : originalBody;

    // Update the draft with the combined body
    await client.api(`/me/messages/${draft.id}`).patch({
        body: {
            content: combinedBody,
            contentType: 'html'
        }
    });

    // Add attachments if any
    if (attachments.length > 0) {
        await Promise.all(attachments.map(attachment =>
            client.api(`/me/messages/${draft.id}/attachments`).post(transformAttachment(attachment))
        ));
    }

    // Send the reply
    await client.api(`/me/messages/${draft.id}/send`).post();

    return {id: draft.id, internetMessageId: draft.internetMessageId};
}

async function createDraftReply({ messageId, accesstoken, toRecipients, ccRecipients, body, attachment }) {
    const client = graph.Client.init({ authProvider: (done) => { done(null, accesstoken); } });
    
    // Create reply draft without body first - this allows Microsoft Graph to automatically include the original message
    const draft = await client.api(`/me/messages/${messageId}/createReply`).post({
        message: {
            toRecipients: toRecipients.map(r => ({ emailAddress: r })),
            ccRecipients: ccRecipients?.map(r => ({ emailAddress: r })),
        },
    });

    // Fetch the draft to get the body with the original message included
    const draftWithOriginal = await client.api(`/me/messages/${draft.id}`)
        .select('body')
        .headers({
            'Prefer': 'outlook.body-content-type="html"'
        })
        .get();

    // Prepend our new reply body to the existing body (which includes the original message)
    const originalBody = draftWithOriginal.body?.content || '';
    const combinedBody = body ? `${body}${originalBody}` : originalBody;

    // Update the draft with the combined body
    await client.api(`/me/messages/${draft.id}`).patch({
        body: {
            content: combinedBody,
            contentType: 'html'
        }
    });

    // Add attachment if provided
    if (attachment) {
        await client.api(`/me/messages/${draft.id}/attachments`).post(transformAttachment(attachment));
    }

    return draft;
}

async function getAttachmentUrl({ messageId, attachment, accesstoken, monadaAccesstoken, monadaOrganizationId }) {
    const { data } = await axios.post(`/api/microsoft/download-attachment?accesstoken=${monadaAccesstoken}&organizationId=${monadaOrganizationId}`,
        {
            messageId,
            attachmentId: attachment.id,
            msAccessToken: accesstoken
        });
    return data.url;
}

function transformAttachment(attachment) {
    return {
        '@odata.type': '#microsoft.graph.fileAttachment',
        name: attachment.name,
        contentBytes: attachment.data,
        contentType: attachment.type,
    }
}

async function trash({ conversationId, accesstoken }) {
    const client = graph.Client.init({ authProvider: (done) => { done(null, accesstoken); } });
    // Delete all messages in the conversation
    const messages = await client.api(`/me/messages?$filter=conversationId eq '${conversationId}'`).get();
    await Promise.all(messages.value.map(message => 
        client.api(`/me/messages/${message.id}`).delete()
    ));
}

async function archive({ conversationId, accesstoken }) {
    const client = graph.Client.init({ authProvider: (done) => { done(null, accesstoken); } });
    // Get all messages in the conversation
    const messages = await client.api(`/me/messages?$filter=conversationId eq '${conversationId}'`).get();
    // Move each message to archive folder
    await Promise.all(messages.value.map(message =>
        client.api(`/me/messages/${message.id}/move`).post({
            destinationId: 'archive'
        })
    ));
}

async function markAsRead({ messageIds, accesstoken }) {
    const client = graph.Client.init({ authProvider: (done) => { done(null, accesstoken); } });

    // Mark each message as read by setting isRead property to true
    await Promise.all(messageIds.map(messageId =>
        client.api(`/me/messages/${messageId}`).patch({
            isRead: true
        })
    ));
}

async function markAsUnread({ messageIds, accesstoken }) {
    if (!messageIds || messageIds.length === 0) {
        console.warn('[outlook] markAsUnread - no message IDs provided');
        return Promise.resolve();
    }
    
    const client = graph.Client.init({ authProvider: (done) => { done(null, accesstoken); } });

    // Mark each message as unread by setting isRead property to false
    // Use Promise.allSettled to handle individual failures gracefully
    const results = await Promise.allSettled(messageIds.map(messageId => {
        if (!messageId) {
            console.warn('[outlook] markAsUnread - skipping invalid messageId:', messageId);
            return Promise.resolve();
        }
        return client.api(`/me/messages/${messageId}`).patch({
            isRead: false
        });
    }));
    
    // Check for any failures and log them
    const failures = results.filter((result, index) => result.status === 'rejected');
    if (failures.length > 0) {
        console.error('[outlook] markAsUnread - some messages failed:', failures.length, 'out of', results.length);
        failures.forEach((failure, index) => {
            const failedIndices = [];
            results.forEach((r, i) => {
                if (r.status === 'rejected') failedIndices.push(i);
            });
            if (failedIndices[index] !== undefined) {
                console.error(`[outlook] markAsUnread - failed for messageId: ${messageIds[failedIndices[index]]}`, failure.reason);
            }
        });
        // If all failed, throw an error
        if (failures.length === results.length) {
            const firstError = failures[0].reason;
            throw new Error(`Failed to mark all messages as unread: ${firstError?.message || firstError?.toString() || 'Unknown error'}`);
        }
    }
}

async function getFolders({ accesstoken }) {
    const client = graph.Client.init({ authProvider: (done) => { done(null, accesstoken); } });
    
    // Fetch all folders (handle pagination)
    let allFolders = [];
    let response = await client.api('/me/mailFolders')
        .select('displayName,id,parentFolderId')
        .get();
    
    allFolders = [...(response.value || [])];
    
    // Handle pagination if there are more folders
    while (response['@odata.nextLink']) {
        response = await client.api(response['@odata.nextLink']).get();
        allFolders = [...allFolders, ...(response.value || [])];
    }

    console.log('[outlook] getFolders', { folders: allFolders });

    // Explicitly fetch the inbox folder using well-known folder endpoint
    // This ensures it's included even if it wasn't in the list above
    let inboxFolder = null;
    try {
        inboxFolder = await client.api('/me/mailFolders/inbox')
            .select('displayName,id,parentFolderId')
            .get();
        console.log('[outlook] getFolders - inbox folder', inboxFolder);
    } catch (e) {
        console.log('[outlook] error fetching inbox folder', e);
    }

    // Create a map of existing folders by ID to avoid duplicates
    const folderMap = new Map();
    allFolders.forEach(f => {
        folderMap.set(f.id, {
            id: f.id,
            name: f.displayName,
        });
    });

    // Add inbox folder if it's not already in the list
    if (inboxFolder && !folderMap.has(inboxFolder.id)) {
        folderMap.set(inboxFolder.id, {
            id: inboxFolder.id,
            name: inboxFolder.displayName,
        });
    }

    return [
        ...Array.from(folderMap.values()),
        { id: 'flagged', name: 'Flagged' },
    ];
}

async function getDefaultFolderId({ folders, accesstoken }) {
    // First, try to fetch the inbox folder using the well-known folder endpoint
    // This works regardless of the folder's display name (e.g., "BANDEJA de ENTRADE")
    if (accesstoken) {
        try {
            const client = graph.Client.init({ authProvider: (done) => { done(null, accesstoken); } });
            const inboxFolder = await client.api('/me/mailFolders/inbox')
                .select('id,displayName')
                .get();
            if (inboxFolder?.id) {
                return inboxFolder.id;
            }
        } catch (e) {
            console.log('[outlook] error fetching inbox folder by well-known name', e);
            // Fall through to display name matching
        }
    }
    
    // Fallback: try to find by display name (for backwards compatibility)
    const inboxById = folders.find(f => f.name?.toLowerCase() === 'inbox');
    if (inboxById?.id) {
        return inboxById.id;
    }
    
    // Last resort: return first folder
    return folders[0]?.id || null;
}

async function getLabels({ accesstoken }) {
    const client = graph.Client.init({ authProvider: (done) => { done(null, accesstoken); } });
    const response = await client.api('/me/outlook/masterCategories')
        .get();
    return response.value.map(category => ({
        id: `category_${category.displayName}`,
        name: category.displayName,
        color: category.color
    }));
}

async function getConversations({ accesstoken, folder, search, nextPageToken }) {
    try {
        let res = null;
        console.log('[outlook] getConversations', accesstoken, folder, search, nextPageToken);
        const client = graph.Client.init({
            authProvider: (done) => done(null, accesstoken),
        });
        const usingSearch = Boolean(search && search.trim());

        // Map friendly names to well-known folder ids
        const mappedFolder = ({
            inbox: 'inbox',
            sent: 'sentitems',
            draft: 'drafts',
            flagged: 'inbox', // special case handled below
        }[folder]) || folder;

        const isFlagged = folder === 'flagged';
        const isCategory = typeof folder === 'string' && folder.startsWith('category_');
        const categoryId = isCategory ? folder.slice('category_'.length) : null;

        if (!nextPageToken) {

            // Prefer scoping by path to avoid parentFolderId filters
            const canUseFolderPath = !isCategory && !!mappedFolder;
            const basePath = canUseFolderPath
                ? `/me/mailFolders/${encodeURIComponent(mappedFolder)}/messages`
                : `/me/messages`;

            // Build query
            let qs = [];
            if (usingSearch) {
                // ❌ no $orderby with $search
                qs.push(`$top=200`);
                qs.push(`$search=${encodeURIComponent(`"${search.trim()}"`)}`);
            } else {
                // ❌ no $orderby with category filters (causes "restriction or sort order is too complex" error)
                // Only add $orderby if not filtering by category
                if (!isCategory) {
                    qs.push(`$orderby=receivedDateTime desc`);
                }
                if (isFlagged) {
                    qs.push(`$top=200`);
                } else {
                    qs.push(`$top=200`);
                }

                const filterParts = [];
                if (isCategory && categoryId) {
                    filterParts.push(`categories/any(c: c eq '${categoryId.replace(/'/g, "''")}')`);
                }
                if (!canUseFolderPath && mappedFolder && !isFlagged && !isCategory) {
                    filterParts.push(`parentFolderId eq '${String(mappedFolder).replace(/'/g, "''")}'`);
                }
                if (filterParts.length) qs.push(`$filter=${filterParts.join(' and ')}`);
            }

            const endpoint = `${basePath}?${qs.join('&')}`;

            // Execute
            let req = client.api(endpoint)
                .select('id,conversationId,subject,bodyPreview,receivedDateTime,from,toRecipients,ccRecipients,flag,categories,isRead,hasAttachments');

            if (usingSearch) req = req.header('ConsistencyLevel', 'eventual');

            res = await req.get();
        } else {
            res = await client.api(nextPageToken).get();
        }
        let items = res.value || [];

        // With $search we can't server-filter, so do it client-side
        if (usingSearch) {
            if (isCategory && categoryId) items = items.filter(m => (m.categories || []).includes(categoryId));
            // And sort by receivedDateTime desc client-side
            items.sort((a, b) => new Date(b.receivedDateTime) - new Date(a.receivedDateTime));
        }
        // When filtering by category, we can't use $orderby, so sort client-side
        if (isCategory && !usingSearch) {
            items.sort((a, b) => new Date(b.receivedDateTime) - new Date(a.receivedDateTime));
        }
        if (isFlagged) items = items.filter(m => m?.flag?.flagStatus === 'flagged');

        // Group messages by conversation
        const conversationMap = {};

        // Helper to transform message data into app format
        const transformMsg = (message) => ({
            ...message,
            snippet: message.bodyPreview,
            sender: message.from ? {
                emailAddress: { address: message.from.emailAddress.address },
                name: message.from.emailAddress.name
            } : null,
            toRecipients: message.toRecipients?.map(r => ({
                emailAddress: { address: r.emailAddress.address },
                name: r.emailAddress.name
            })),
            ccRecipients: message.ccRecipients?.map(r => ({
                emailAddress: { address: r.emailAddress.address },
                name: r.emailAddress.name
            })),
            flagged: message.flag?.flagStatus === 'flagged',
            unread: !message.isRead,
            hasAttachments: message.hasAttachments || false,
            attachments: message.attachments || []
        });

        items.forEach(message => {
            if (!conversationMap[message.conversationId]) {
                conversationMap[message.conversationId] = [];
            }
            conversationMap[message.conversationId].push(transformMsg(message));
        });

        // ---------------------------------------------------------
        // START FIX: Batch Request implementation to prevent 429
        // ---------------------------------------------------------
        
        const uniqueConversationIds = Object.keys(conversationMap).filter(id => id); 
        
        // Prepare relative URLs for the batch request
        const batchUrls = uniqueConversationIds.map(conversationId => {
            const safeId = String(conversationId).replace(/'/g, "''");
            return `/me/messages?$filter=conversationId eq '${safeId}'&$select=id,conversationId,subject,bodyPreview,receivedDateTime,from,toRecipients,ccRecipients,flag,categories,isRead&$top=50`;
        });

        if (batchUrls.length > 0) {
            // Process requests in batches of 20 using the helper
            const batchResponses = await processBatchRequests(client, batchUrls);

            // Map results back to the conversation map
            batchResponses.forEach((response) => {
                if (response.status === 200 && response.body && response.body.value) {
                    const messages = response.body.value;
                    if (messages.length > 0) {
                        const convId = messages[0].conversationId;
                        
                        // If we got fresh data, update the map
                        if (conversationMap[convId]) {
                             conversationMap[convId] = messages.map(m => transformMsg(m));
                        }
                    }
                }
            });
        }
        // ---------------------------------------------------------
        // END FIX
        // ---------------------------------------------------------

        // Sort conversations by most recent message (latest message in each conversation)
        let conversations = Object.values(conversationMap);
        conversations.sort((a, b) => {
            // Find the latest message in each conversation
            const aLatest = a.reduce((latest, msg) => {
                const msgDate = new Date(msg.receivedDateTime);
                return msgDate > latest ? msgDate : latest;
            }, new Date(0));
            const bLatest = b.reduce((latest, msg) => {
                const msgDate = new Date(msg.receivedDateTime);
                return msgDate > latest ? msgDate : latest;
            }, new Date(0));
            return bLatest - aLatest; // Sort descending (newest first)
        });

        console.log('[outlook] getConversations results', conversations, res);

        // Extract nextPageToken from @odata.nextLink (if present)
        const newNext = res['@odata.nextLink'];

        return {
            conversations,
            nextPageToken: newNext ? newNext : null
        };
    } catch (e) {
        console.log('[outlook] error with getConversations', e);
        // Check if this is an expired token error
        const errorMessage = e?.message || e?.error?.message || JSON.stringify(e);
        const isExpiredError = errorMessage?.includes('Lifetime validation failed') || 
                              errorMessage?.includes('expired') ||
                              errorMessage?.includes('InvalidAuthenticationToken') ||
                              e?.statusCode === 401 ||
                              e?.code === 'InvalidAuthenticationToken';
        
        if (isExpiredError && _expiredListener) {
            console.log('[outlook] Token expired, calling expired listener');
            _expiredListener();
            // Don't throw the error - it's been handled by the listener
            // Return null to indicate no conversations could be fetched due to expired token
            return { conversations: [], nextPageToken: null };
        }
        throw e;
    }
}

async function setConversationFlag({ conversationId, accesstoken, flagged }) {
    const client = graph.Client.init({ authProvider: (done) => { done(null, accesstoken); } });
    
    // First get all messages in the conversation
    const messages = await client
        .api('/me/messages')
        .filter(`conversationId eq '${conversationId}'`)
        .get();

    // Update flag status for all messages
    await Promise.all(messages.value.map(message => {
        return client.api(`/me/messages/${message.id}`).patch({
            flag: {
                flagStatus: flagged ? 'flagged' : 'notFlagged'
            }
        });
    }));
}

export default { 
    setConversationsListener,
    setExpiredListener,
    LoginButton,
    refreshConnection,
    forceRefreshConnection,
    disconnect,
    getMessagesForConversation,
    createDraft,
    sendEmail,
    sendReply,
    createDraftReply,
    getAttachmentUrl,
    markAsRead,
    markAsUnread,
    trash,
    archive,
    getLabels,
    getFolders,
    getDefaultFolderId,
    getConversations,
    setConversationFlag,
};